/**
 * Session-scoped tracker for Patient Feedback link sends.
 *
 * Stop-gap visual confirmation until the backend exposes engagement send
 * history. Each call to `recordFeedbackSend(...)` prepends an entry; reads
 * return the most recent N entries scoped to the current practice.
 *
 * Storage key is namespaced by practiceId so a user switching practices
 * doesn't see another practice's send activity.
 */
const STORAGE_KEY_PREFIX = 'dental-sem.feedback-sends';
const MAX_ENTRIES = 10;

function storageKey(practiceId) {
    return `${STORAGE_KEY_PREFIX}:${practiceId}`;
}

function readSafe(key) {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
        return [];
    }
}

function writeSafe(key, value) {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_e) {
        /* sessionStorage unavailable or quota — silently drop */
    }
}

export function recordFeedbackSend({ practiceId, leadId, leadName, treatmentId } = {}) {
    if (!practiceId || !leadId) return;
    const key = storageKey(practiceId);
    const existing = readSafe(key);
    const next = [
        {
            leadId,
            leadName: leadName || 'Patient',
            treatmentId: treatmentId || null,
            sentAt: new Date().toISOString(),
        },
        ...existing.filter((e) => e.leadId !== leadId),
    ].slice(0, MAX_ENTRIES);
    writeSafe(key, next);
}

export function getRecentFeedbackSends(practiceId) {
    if (!practiceId) return [];
    return readSafe(storageKey(practiceId));
}

export function clearRecentFeedbackSends(practiceId) {
    if (!practiceId) return;
    try {
        sessionStorage.removeItem(storageKey(practiceId));
    } catch (_e) {
        /* silently drop */
    }
}

/**
 * Removes ALL feedback-send entries across every practice from sessionStorage.
 * Called from the auth logout flow so a subsequent user on the same browser
 * tab can't see the previous user's recent-send activity (multi-user laptop
 * + same-session login scenario).
 */
export function clearAllRecentFeedbackSends() {
    try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith(`${STORAGE_KEY_PREFIX}:`)) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch (_e) {
        /* silently drop */
    }
}
